import React, { PropsWithChildren, useEffect, useRef, useState } from 'react';
import componentProps, {
  ComponentProps,
  componentPropsWithConcatenatedDataCy,
  componentPropsWithoutDataCy,
} from '../../helpers/componentProps';
import { scrollToElement } from '../../helpers/scroll';
import { applyComponentTheme } from '../../theme';
import { GridItemProps } from '../Grid';
import { defaultGridItemProps } from './layout';
import {
  BottomMargin,
  ChildWrapper,
  ListWithPaginationGrid,
  ListWithPaginationGridItem,
  ListWithPaginationWrapper,
  PaginationButtons,
  PaginationDivider,
} from './styles';
import usePaginationListFocus from './usePaginationListFocus';

export type ListWithPaginationProps = {
  itemsToDisplayCount: number;
  displayDividers: boolean;
  gridItemProps?: GridItemProps;
  fixedHeaderOffset?: number;
  enforceBottomSpaceConsistency?: boolean;
  customBottomSpacing?: BottomMargin;
  dataCy?: {
    paginationButtons?: string;
    paginationItemList?: string;
  };
} & ComponentProps;

const ListWithPagination: React.FC<PropsWithChildren<ListWithPaginationProps>> = ({
  itemsToDisplayCount,
  children,
  displayDividers,
  gridItemProps,
  fixedHeaderOffset,
  enforceBottomSpaceConsistency,
  customBottomSpacing,
  dataCy,
  ...props
}) => {
  const [currentPage, setCurrentPage] = useState<number>(1);
  const paginationListRef = usePaginationListFocus(currentPage);
  const listWithPaginationGrid = useRef<HTMLDivElement>(null);
  const listItemCount = React.Children.count(children);
  const pageCount = Math.ceil(listItemCount / itemsToDisplayCount);
  const columnsByBreakpoint: GridItemProps = {
    desktop: gridItemProps?.desktop ?? defaultGridItemProps?.desktop,
    tabletLandscape:
      gridItemProps?.tabletLandscape ?? defaultGridItemProps?.tabletLandscape,
    tabletPortrait: gridItemProps?.tabletPortrait ?? defaultGridItemProps?.tabletPortrait,
    mobile: gridItemProps?.mobile ?? defaultGridItemProps?.mobile,
  };
  const isListGreaterThanDisplayCount = listItemCount > itemsToDisplayCount;

  const isInitialRender = useRef(true);
  useEffect(() => {
    if (isInitialRender.current) {
      isInitialRender.current = false;
    } else if (listWithPaginationGrid.current) {
      scrollToElement(listWithPaginationGrid.current, fixedHeaderOffset ?? 0);
    }
  }, [currentPage, fixedHeaderOffset]);

  const shouldShowTopDivider = (index: number): boolean => displayDividers && index === 0;

  useEffect(() => {
    setCurrentPage(1);
  }, [children]);

  return (
    <ListWithPaginationWrapper ref={paginationListRef}>
      <ListWithPaginationGrid
        alignLeft
        {...componentProps(props)}
        ref={listWithPaginationGrid}
        enforceBottomSpaceConsistency={enforceBottomSpaceConsistency}
        customBottomSpacing={customBottomSpacing}
        isListGreaterThanDisplayCount={isListGreaterThanDisplayCount}>
        {React.Children.toArray(children)
          .slice(
            (currentPage - 1) * itemsToDisplayCount,
            currentPage * itemsToDisplayCount
          )
          .map((child, index) => {
            const dividerProps = componentPropsWithoutDataCy(props);
            return (
              <ListWithPaginationGridItem
                key={`${currentPage}-${index}`}
                displayDividers={displayDividers}
                {...columnsByBreakpoint}
                {...componentPropsWithConcatenatedDataCy(props, 'PaginationListItem')}>
                {shouldShowTopDivider(index) && <PaginationDivider {...dividerProps} />}
                <ChildWrapper displayDividers={displayDividers}>{child}</ChildWrapper>
                {displayDividers && <PaginationDivider {...dividerProps} />}
              </ListWithPaginationGridItem>
            );
          })}
      </ListWithPaginationGrid>
      {isListGreaterThanDisplayCount && (
        <PaginationButtons
          totalPages={pageCount}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          customBottomSpacing={customBottomSpacing}
          {...componentPropsWithConcatenatedDataCy(props, 'PaginationButtons')}
        />
      )}
    </ListWithPaginationWrapper>
  );
};

export default applyComponentTheme(ListWithPagination, 'ListWithPagination');
